import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
  Alert,
  Container,
  Avatar,
  Tooltip,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';

import {
  useGetCommunityPosts,
  useGetReplies,
  PostResponse,
  useTogglePostLike,
  useCreateReply,
  useToggleReplyLike,
  ReplyResponse,
} from '@/api/endpoints/community/posts';
import { useApproveMembershipRequest } from '@/api/endpoints/community/useApproveMembershipRequest';
import { useGetCommunity } from '@/api/endpoints/community/useGetCommunity';
import { useGetCommunityActivities } from '@/api/endpoints/community/useGetCommunityActivities';
import { useGetGroupNegotiations } from '@/api/endpoints/community/useGetGroupNegotiations';
import { useJoinCommunity } from '@/api/endpoints/community/useJoinCommunity';
import { useLeaveCommunity } from '@/api/endpoints/community/useLeaveCommunity';
import { useRejectMembershipRequest } from '@/api/endpoints/community/useRejectMembershipRequest';
import { useRemoveMember } from '@/api/endpoints/community/useRemoveMember';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { CheckIcon } from '@/assets/icons/CheckIcon';
import { DotsVerticalIcon } from '@/assets/icons/DotsVerticalIcon';
import { EditIcon } from '@/assets/icons/EditIcon';
import { KeyboardReturnIcon } from '@/assets/icons/KeyboardReturnIcon';
import { LogOut01Icon } from '@/assets/icons/LogOut01Icon';
import { MessageCircle01Icon } from '@/assets/icons/MessageCircle01Icon';
import { PiggyBank02Icon } from '@/assets/icons/PiggyBank02Icon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { Settings01Icon } from '@/assets/icons/Settings01Icon';
import { Shield01Icon } from '@/assets/icons/Shield01Icon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { User01Icon } from '@/assets/icons/User01Icon';
import { Users02Icon } from '@/assets/icons/Users02Icon';
import { XIcon } from '@/assets/icons/XIcon';
import { PMPRoutes } from '@/config/routes';

import CommunityCalendarView from '@/components/community/CommunityCalendarView';
import CommunityContactsTab from '@/components/community/CommunityContactsTab';
import CommunitySettingsModal from '@/components/community/CommunitySettingsModal';
import EditRulesModal from '@/components/community/EditRulesModal';
import GroupNegotiationsSection from '@/components/community/GroupNegotiationsSection';
import RecentActivity from '@/components/community/RecentActivity';
import ShareCommunityModal from '@/components/community/ShareCommunityModal';
import { CommunityPostsTab } from '@/components/posts';
import { PostForm, PostFormData } from '@/components/posts/PostForm';

// Component for individual post discussion
const PostDiscussion: React.FC<{
  post: PostResponse;
  index: number;
  totalPosts: number;
  communityId: number;
  replyInputs: { [postId: number]: string };
  showReplyForms: { [postId: number]: boolean };
  expandedReplies: Record<string, boolean>;
  onTogglePostLike: (postId: number) => void;
  onShowReplyForm: (postId: number, show: boolean) => void;
  onReplyInputChange: (postId: number, value: string) => void;
  onSubmitReply: (postId: number) => void;
  onToggleExpandedReplies: (postId: number) => void;
  onToggleReplyLike: (replyId: number, postId: number) => void;
  getAvatarProps: (name: string) => { children: string };
  togglePostLikeMutation: { isPending: boolean };
  createReplyMutation: ReturnType<typeof import('@/api/endpoints/community/posts').useCreateReply>;
}> = ({
  post,
  index,
  totalPosts,
  communityId,
  replyInputs,
  showReplyForms,
  expandedReplies,
  onTogglePostLike,
  onShowReplyForm,
  onReplyInputChange,
  onSubmitReply,
  onToggleExpandedReplies,
  onToggleReplyLike,
  getAvatarProps,
  togglePostLikeMutation,
  createReplyMutation,
}) => {
  // State for reply-to-reply functionality
  const [replyToReplyForms, setReplyToReplyForms] = useState<{ [replyId: number]: boolean }>({});
  const [replyToReplyInputs, setReplyToReplyInputs] = useState<{ [replyId: number]: string }>({});

  const handleShowReplyToReplyForm = (replyId: number, show: boolean) => {
    if (show) {
      // Close all other reply forms and open only this one
      setReplyToReplyForms({ [replyId]: true });
      setReplyToReplyInputs({ [replyId]: '' });
    } else {
      // Close this specific reply form
      setReplyToReplyForms(prev => ({ ...prev, [replyId]: false }));
      setReplyToReplyInputs(prev => ({ ...prev, [replyId]: '' }));
    }
  };

  const handleReplyToReplyInputChange = (replyId: number, value: string) => {
    setReplyToReplyInputs(prev => ({ ...prev, [replyId]: value }));
  };

  const handleSubmitReplyToReply = async (replyId: number) => {
    const content = replyToReplyInputs[replyId]?.trim();
    if (!content) return;

    try {
      await createReplyMutation.mutateAsync({
        postId: post.id,
        communityId,
        content,
        parentId: replyId,
      });
      handleShowReplyToReplyForm(replyId, false);
    } catch (error) {
      // Error handling is managed by the mutation
      // eslint-disable-next-line no-console
      console.error('Failed to create reply:', error);
    }
  };

  // Override the main reply form handler to close reply-to-reply forms
  const handleShowMainReplyForm = (postId: number, show: boolean) => {
    if (show) {
      // Close all reply-to-reply forms when opening main reply form
      setReplyToReplyForms({});
      setReplyToReplyInputs({});
    }
    onShowReplyForm(postId, show);
  };
  const { data: replies = [] } = useGetReplies({ postId: post.id, page: 1, limit: 3 }, true);

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const postDate = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - postDate.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  // Helper function to render all replies in a flat structure
  const renderAllReplies = (repliesData: ReplyResponse[], postId: number) => {
    const formatTimeAgoLocal = (dateString: string) => {
      const now = new Date();
      const postDate = new Date(dateString);
      const diffInHours = Math.floor((now.getTime() - postDate.getTime()) / (1000 * 60 * 60));

      if (diffInHours < 1) return 'Just now';
      if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    };

    // Flatten all replies into a single array with their levels and parent info
    const flattenReplies = (
      repliesToFlatten: ReplyResponse[],
      parentReply?: ReplyResponse,
      level = 0
    ): Array<{ reply: ReplyResponse; level: number; parentReply?: ReplyResponse }> => {
      const result: Array<{ reply: ReplyResponse; level: number; parentReply?: ReplyResponse }> =
        [];

      repliesToFlatten.forEach(reply => {
        result.push({ reply, level, parentReply });
        if (reply.nested_replies && reply.nested_replies.length > 0) {
          result.push(...flattenReplies(reply.nested_replies, reply, level + 1));
        }
      });

      return result;
    };

    const flattenedReplies = flattenReplies(repliesData);

    return flattenedReplies.map(({ reply, level, parentReply }) => (
      <Box key={reply.id} sx={{ mb: level > 0 ? 1 : 1.5 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          sx={{
            ml: level >= 1 ? 3 : 0,
            pl: level > 1 ? 2 : 0,
          }}
        >
          <Avatar
            {...getAvatarProps(reply.author.user.full_name || reply.author.user.email)}
            sx={{
              width: level === 0 ? 24 : 20,
              height: level === 0 ? 24 : 20,
              fontSize: level === 0 ? '0.75rem' : '0.7rem',
              fontWeight: 600,
              bgcolor: alpha('#9D1500', 0.2),
              color: 'primary.main',
              mt: 0.25,
            }}
          />
          <Box sx={{ flex: 1 }}>
            {/* Reply Header with Reply Indicator */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'secondary.900',
                  fontSize: level > 0 ? '0.7rem' : '0.75rem',
                  display: 'inline',
                }}
              >
                {reply.author.user.full_name || reply.author.user.email}
              </Typography>
              {level > 0 && parentReply && (
                <>
                  <KeyboardReturnIcon
                    sx={{
                      width: 12,
                      height: 12,
                      color: 'secondary.500',
                      transform: 'scaleX(-1)',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: 'secondary.900',
                      fontSize: '0.7rem',
                      display: 'inline',
                    }}
                  >
                    {parentReply.author.user.full_name || parentReply.author.user.email}
                  </Typography>
                </>
              )}
            </Stack>

            {/* Reply Content */}
            <Typography
              variant="body2"
              sx={{
                color: 'secondary.800',
                fontSize: level > 0 ? '0.7rem' : '0.75rem',
                display: 'block',
                mb: 0.5,
              }}
            >
              {reply.content}
            </Typography>

            {/* Reply Actions */}
            <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'secondary.600',
                  fontSize: '0.7rem',
                }}
              >
                {formatTimeAgoLocal(reply.created_at)}
              </Typography>
              <Button
                size="small"
                onClick={() => onToggleReplyLike(reply.id, postId)}
                disabled={false}
                sx={{
                  color: reply.is_liked ? 'primary.main' : 'secondary.600',
                  textTransform: 'none',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  p: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: 'primary.main',
                  },
                }}
              >
                {reply._count.likes > 0 ? `${reply._count.likes} ` : ''}Like
              </Button>
              <Button
                size="small"
                onClick={() => handleShowReplyToReplyForm(reply.id, !replyToReplyForms[reply.id])}
                sx={{
                  color: 'secondary.600',
                  textTransform: 'none',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  p: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: 'primary.main',
                  },
                }}
              >
                Reply
              </Button>
            </Stack>

            {/* Reply-to-Reply Form */}
            {replyToReplyForms[reply.id] && (
              <Box sx={{ mt: 1, mb: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <TextField
                    placeholder="Write a reply..."
                    value={replyToReplyInputs[reply.id] || ''}
                    onChange={e => handleReplyToReplyInputChange(reply.id, e.target.value)}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.75rem',
                        borderRadius: 2,
                        minHeight: 'auto',
                        height: 'auto',
                        '& .MuiInputBase-input': {
                          padding: '0px 8px',
                          lineHeight: 1.2,
                          height: '34px',
                        },
                      },
                    }}
                    onKeyPress={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitReplyToReply(reply.id);
                      }
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => handleSubmitReplyToReply(reply.id)}
                    disabled={!replyToReplyInputs[reply.id]?.trim()}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                      '&:disabled': {
                        bgcolor: 'secondary.300',
                        color: 'secondary.500',
                      },
                    }}
                  >
                    Post
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    ));
  };

  return (
    <Box sx={{ mb: index < totalPosts - 1 ? 3 : 0 }}>
      {/* Post Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
        <Avatar
          {...getAvatarProps(post.author.user.full_name || post.author.user.email)}
          sx={{
            width: 32,
            height: 32,
            fontSize: '0.875rem',
            fontWeight: 600,
            bgcolor: 'primary.main',
            color: 'white',
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'secondary.900',
              fontSize: '0.875rem',
            }}
          >
            {post.author.user.full_name || post.author.user.email}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'secondary.600',
              fontSize: '0.7rem',
            }}
          >
            {formatTimeAgo(post.created_at)}
          </Typography>
        </Box>
      </Stack>

      {/* Post Content */}
      <Typography
        variant="body2"
        sx={{
          color: 'secondary.800',
          lineHeight: 1.5,
          mb: 1.5,
          fontSize: '0.875rem',
        }}
      >
        {post.title && (
          <Box component="span" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            {post.title}
          </Box>
        )}
        {post.content}
      </Typography>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
        <Button
          size="small"
          onClick={() => onTogglePostLike(post.id)}
          disabled={togglePostLikeMutation.isPending}
          sx={{
            color: post.is_liked ? 'primary.main' : 'secondary.600',
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 500,
            p: 0,
            minWidth: 'auto',
            '&:hover': {
              bgcolor: 'transparent',
              color: 'primary.main',
            },
          }}
        >
          {post._count.likes > 0 ? `${post._count.likes} ` : ''}Like
        </Button>
        <Button
          size="small"
          onClick={() => handleShowMainReplyForm(post.id, !showReplyForms[post.id])}
          sx={{
            color: 'secondary.600',
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 500,
            p: 0,
            minWidth: 'auto',
            '&:hover': {
              bgcolor: 'transparent',
              color: 'primary.main',
            },
          }}
        >
          {post._count.replies > 0 ? `${post._count.replies} ` : ''}Reply
        </Button>
      </Stack>

      {/* Reply Form */}
      {showReplyForms[post.id] && (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              placeholder="Write a reply..."
              value={replyInputs[post.id] || ''}
              onChange={e => onReplyInputChange(post.id, e.target.value)}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.75rem',
                  borderRadius: 2,
                  minHeight: 'auto',
                  height: 'auto',
                  '& .MuiInputBase-input': {
                    padding: '0px 8px',
                    lineHeight: 1.2,
                    height: '34px',
                  },
                },
              }}
              onKeyPress={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSubmitReply(post.id);
                }
              }}
            />
            <Button
              size="small"
              onClick={() => onSubmitReply(post.id)}
              disabled={!replyInputs[post.id]?.trim() || createReplyMutation.isPending}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.75rem',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'secondary.300',
                  color: 'secondary.500',
                },
              }}
            >
              {createReplyMutation.isPending ? 'Posting...' : 'Post'}
            </Button>
          </Stack>
        </Box>
      )}

      {/* Comments Section */}
      {replies.length > 0 && (
        <Box>
          {renderAllReplies(expandedReplies[post.id] ? replies : replies.slice(0, 2), post.id)}
          {replies.length > 2 && (
            <Button
              size="small"
              onClick={() => onToggleExpandedReplies(post.id)}
              sx={{
                color: 'secondary.600',
                textTransform: 'none',
                fontSize: '0.7rem',
                fontWeight: 500,
                p: 0,
                minWidth: 'auto',
                mt: 0.5,
                '&:hover': {
                  bgcolor: 'transparent',
                  color: 'primary.main',
                },
              }}
            >
              {expandedReplies[post.id]
                ? 'Show less'
                : `View ${replies.length - 2} more replies...`}
            </Button>
          )}
        </Box>
      )}

      {/* Divider between posts */}
      {index < totalPosts - 1 && (
        <Box
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            mt: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 3 },
            mx: { xs: -2, sm: 0 },
          }}
        />
      )}
    </Box>
  );
};

// Define tab names as constants to avoid repetition
const TAB_NAMES = {
  HOME: 'home',
  MEMBERS: 'members',
  CALENDAR: 'calendar',
  NEGOTIATIONS: 'negotiations',
  CONTACTS: 'contacts',
} as const;

type TabName = (typeof TAB_NAMES)[keyof typeof TAB_NAMES];

const CommunityViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation();

  // Check if user came from a shared link
  const [cameFromSharedLink, setCameFromSharedLink] = useState(false);
  const [pendingJoinStatus, setPendingJoinStatus] = useState<{
    isPending: boolean;
    communityName?: string;
  }>({ isPending: false });

  useEffect(() => {
    // Check if there's a referrer from share link in session storage
    const shareRedirectUrl = localStorage.getItem('shareRedirectUrl');
    if (shareRedirectUrl && shareRedirectUrl.includes('/share/')) {
      setCameFromSharedLink(true);
      // Clear it after checking
      localStorage.removeItem('shareRedirectUrl');
    }

    // Also check if the referrer contains share link
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('from') === 'share' || urlParams.get('campaign') === 'share_link') {
      setCameFromSharedLink(true);
    }

    // Check if there's a pending join status in localStorage
    const pendingStatus = localStorage.getItem(`pendingCommunity_${id}`);
    if (pendingStatus) {
      try {
        const parsed = JSON.parse(pendingStatus);
        // Only restore if less than 24 hours old
        if (parsed.isPending && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          setPendingJoinStatus({ isPending: true });
          setCameFromSharedLink(true); // Also set this to show the appropriate UI
        } else {
          // Clear old pending status
          localStorage.removeItem(`pendingCommunity_${id}`);
        }
      } catch (e) {
        // Invalid data, clear it
        localStorage.removeItem(`pendingCommunity_${id}`);
      }
    }
  }, [location, id]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get tab from URL search params, default to 'home'
  const getTabFromUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    const validTabs = Object.values(TAB_NAMES);
    if (tab && validTabs.includes(tab as TabName)) {
      return tab as TabName;
    }
    return TAB_NAMES.HOME;
  };

  const [selectedTab, setSelectedTab] = useState<TabName>(getTabFromUrl());

  // Update tab when URL changes
  useEffect(() => {
    const getTabFromUrlEffect = () => {
      const urlParams = new URLSearchParams(location.search);
      const tab = urlParams.get('tab');
      if (tab && Object.values(TAB_NAMES).includes(tab as TabName)) {
        return tab as TabName;
      }
      return TAB_NAMES.HOME;
    };

    setSelectedTab(getTabFromUrlEffect());
  }, [location.search]);

  // Handle tab change and update URL
  const handleTabChange = (newTab: TabName) => {
    const urlParams = new URLSearchParams(location.search);
    if (newTab === TAB_NAMES.HOME) {
      urlParams.delete('tab'); // Remove tab param for default tab
    } else {
      urlParams.set('tab', newTab);
    }
    const newSearch = urlParams.toString();
    const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    history.push(newPath);
  };

  const [editRulesModalOpen, setEditRulesModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [leaveConfirmDialogOpen, setLeaveConfirmDialogOpen] = useState(false);
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false);
  const [shareCommunityModalOpen, setShareCommunityModalOpen] = useState(false);
  const [communityInfoOpen, setCommunityInfoOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [recentActivityOpen, setRecentActivityOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{
    id: number;
    name: string;
    email: string;
  } | null>(null);
  const [memberMenuAnchor, setMemberMenuAnchor] = useState<null | HTMLElement>(null);
  const [communityMenuAnchor, setCommunityMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<{
    membership_id: number;
    name: string;
    email: string;
    id: string;
  } | null>(null);

  const [replyInputs, setReplyInputs] = useState<{ [postId: number]: string }>({});
  const [showReplyForms, setShowReplyForms] = useState<{ [postId: number]: boolean }>({});
  const [expandedReplies, setExpandedReplies] = useState<Record<string, boolean>>({});
  const [showCreatePostForm, setShowCreatePostForm] = useState(false);

  const {
    data: community,
    error,
    isLoading,
  } = useGetCommunity({
    id: parseInt(id, 10),
  });

  // Check if the response indicates pending membership
  // The API returns membership_status when user has pending membership
  const isPendingMembership =
    community?.user_status === 'PENDING' ||
    (community &&
      'membership_status' in community &&
      (community as { membership_status?: string }).membership_status === 'PENDING');

  // Get the pending message from API response if available
  const pendingMessage =
    community && 'message' in community ? (community as { message?: string }).message : null;

  const { data: groupNegotiations, isLoading: negotiationsLoading } = useGetGroupNegotiations(
    {
      communityId: parseInt(id, 10),
    },
    !!community
  );

  const { data: activities, isLoading: activitiesLoading } = useGetCommunityActivities(
    {
      communityId: parseInt(id, 10),
    },
    !!community
  );

  const { data: posts = [] } = useGetCommunityPosts({
    communityId: parseInt(id, 10),
    page: 1,
    limit: 3,
  });

  const leaveCommunityMutation = useLeaveCommunity();
  const joinCommunityMutation = useJoinCommunity({
    onError: () => {
      // Silent error handling - we'll handle the UI update in handleJoinCommunity
      // Don't show the default error notification
    },
  });
  const approveMembershipMutation = useApproveMembershipRequest();
  const rejectMembershipMutation = useRejectMembershipRequest();
  const removeMemberMutation = useRemoveMember();

  // Clear pending status if user now has access
  useEffect(() => {
    if (community?.user_role) {
      // User has access, clear any pending status
      localStorage.removeItem(`pendingCommunity_${id}`);
    }
  }, [community?.user_role, id]);

  // Set pending status if backend returns pending membership
  useEffect(() => {
    if (isPendingMembership) {
      setPendingJoinStatus({
        isPending: true,
        communityName: community?.name,
      });
      setCameFromSharedLink(true);
    }
  }, [isPendingMembership, community?.name]);

  const togglePostLikeMutation = useTogglePostLike();
  const createReplyMutation = useCreateReply();
  const toggleReplyLikeMutation = useToggleReplyLike();

  const handleBack = () => {
    history.push(PMPRoutes.myCommunities);
  };

  const handleJoinCommunity = async () => {
    try {
      const result = await joinCommunityMutation.mutateAsync({
        communityId: parseInt(id, 10),
      });

      // Check if the membership is pending (private community requiring approval)
      if (result.status === 'PENDING') {
        // Set pending status with community name if available
        setPendingJoinStatus({
          isPending: true,
          communityName: community?.name,
        });
        // Store in localStorage to persist across refreshes
        localStorage.setItem(
          `pendingCommunity_${id}`,
          JSON.stringify({ isPending: true, timestamp: Date.now() })
        );
      } else {
        // If approved immediately, reload to show community content
        window.location.reload();
      }
    } catch (joinError) {
      // Check if error is due to existing pending request
      const joinErr = joinError as Error;
      const errorMessage = joinErr?.message || '';

      // For any join error that suggests membership issues, show pending status
      // This provides a better UX than showing error messages
      if (
        errorMessage.includes('already') ||
        errorMessage.includes('pending') ||
        errorMessage.includes('not a member') ||
        errorMessage.includes('membership')
      ) {
        // User likely has a pending request already
        setPendingJoinStatus({
          isPending: true,
          communityName: community?.name,
        });
        // Store in localStorage
        localStorage.setItem(
          `pendingCommunity_${id}`,
          JSON.stringify({ isPending: true, timestamp: Date.now() })
        );
      }
    }
  };

  const handleLeaveCommunity = () => {
    setLeaveConfirmDialogOpen(true);
  };

  const handleConfirmLeave = async () => {
    try {
      await leaveCommunityMutation.mutateAsync({
        communityId: parseInt(id, 10),
      });

      setLeaveConfirmDialogOpen(false);
      // Navigate back to communities list after leaving
      history.push(PMPRoutes.myCommunities);
    } catch (leaveError) {
      // Error handling is managed by the mutation
      // eslint-disable-next-line no-console
      console.error('Failed to leave community:', leaveError);
    }
  };

  const handleApproveMember = (membershipId: number) => {
    approveMembershipMutation.mutate({
      communityId: parseInt(id, 10),
      membershipId,
    });
  };

  const handleRejectMember = (membershipId: number) => {
    rejectMembershipMutation.mutate({
      communityId: parseInt(id, 10),
      membershipId,
    });
  };

  const handleRemoveMember = (membershipId: number, memberName: string, memberEmail: string) => {
    setMemberToRemove({
      id: membershipId,
      name: memberName,
      email: memberEmail,
    });
    setRemoveMemberDialogOpen(true);
  };

  const handleConfirmRemoveMember = () => {
    if (memberToRemove) {
      removeMemberMutation.mutate({
        communityId: parseInt(id, 10),
        membershipId: memberToRemove.id,
      });
      setRemoveMemberDialogOpen(false);
      setMemberToRemove(null);
    }
  };

  const handleMemberMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    member: {
      id: string;
      membership_id: number;
      full_name?: string;
      email: string;
    }
  ) => {
    setMemberMenuAnchor(event.currentTarget);
    setSelectedMember({
      membership_id: member.membership_id,
      name: member.full_name || member.email,
      email: member.email,
      id: member.id,
    });
  };

  const handleMemberMenuClose = () => {
    setMemberMenuAnchor(null);
    setSelectedMember(null);
  };

  const handleMemberMenuRemove = () => {
    if (selectedMember) {
      // eslint-disable-next-line no-console
      console.log('Removing member:', selectedMember);
      handleRemoveMember(selectedMember.membership_id, selectedMember.name, selectedMember.email);
    }
    handleMemberMenuClose();
  };

  const handleTogglePostLike = async (postId: number) => {
    await togglePostLikeMutation.mutateAsync({
      postId,
      communityId: parseInt(id, 10),
    });
  };

  const handleCreateReply = async (postId: number, content: string) => {
    await createReplyMutation.mutateAsync({
      postId,
      communityId: parseInt(id, 10),
      content,
    });
  };

  const handleToggleReplyLike = async (replyId: number, postId: number) => {
    await toggleReplyLikeMutation.mutateAsync({
      replyId,
      postId,
      communityId: parseInt(id, 10),
    });
  };

  const handleReplyInputChange = (postId: number, value: string) => {
    setReplyInputs(prev => ({ ...prev, [postId]: value }));
  };

  const handleShowReplyForm = (postId: number, show: boolean) => {
    setShowReplyForms(prev => ({ ...prev, [postId]: show }));
    if (!show) {
      setReplyInputs(prev => ({ ...prev, [postId]: '' }));
    }
  };

  const handleSubmitReply = async (postId: number) => {
    const content = replyInputs[postId]?.trim();
    if (!content) return;

    await handleCreateReply(postId, content);
    handleShowReplyForm(postId, false);
  };

  const handleToggleExpandedReplies = (postId: number) => {
    setExpandedReplies(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  const handleCreatePostClick = () => {
    setShowCreatePostForm(true);
  };

  const handleCreatePost = async (data: PostFormData) => {
    try {
      // For now, we'll use a simple approach since we don't have the useCreatePost hook imported
      // In a real implementation, you would use the actual API hook
      // eslint-disable-next-line no-console
      console.log('Creating post:', data);
      setShowCreatePostForm(false);
      // Refresh the posts list
      window.location.reload();
    } catch (createError) {
      // eslint-disable-next-line no-console
      console.error('Failed to create post:', createError);
    }
  };

  const handleCancelCreatePost = () => {
    setShowCreatePostForm(false);
  };

  const getAvatarProps = (name: string) => {
    const initials = name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);

    return {
      children: initials,
    };
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          width: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Handle pending membership status or error
  if (isPendingMembership || error) {
    // Check if this is a 403 Forbidden error (access denied)
    const isAccessDenied =
      error && (error.message.includes('403') || error.message.includes('Forbidden'));

    if (isPendingMembership || (isAccessDenied && cameFromSharedLink)) {
      // Show the join/pending UI
      if (cameFromSharedLink || isPendingMembership) {
        return (
          <Box sx={{ minHeight: '100vh', py: 4 }}>
            <Container maxWidth="md">
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  background: `linear-gradient(135deg, ${alpha(
                    '#9D1500',
                    0.02
                  )} 0%, transparent 100%)`,
                }}
              >
                {pendingJoinStatus.isPending || isPendingMembership ? (
                  <>
                    <Typography
                      variant="h4"
                      component="h1"
                      sx={{
                        fontWeight: 700,
                        color: 'secondary.900',
                        mb: 2,
                        fontSize: { xs: '1.75rem', sm: '2rem' },
                        textAlign: 'center',
                      }}
                    >
                      Join Request Pending
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        color: 'secondary.700',
                        mb: 3,
                        fontWeight: 500,
                        fontSize: '1.125rem',
                        textAlign: 'center',
                      }}
                    >
                      {pendingMessage || 'Your request to join this community is awaiting approval'}
                    </Typography>

                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: alpha('#ff9800', 0.05),
                        border: '1px solid',
                        borderColor: alpha('#ff9800', 0.2),
                        mb: 4,
                        maxWidth: 480,
                        mx: 'auto',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'secondary.700',
                          lineHeight: 1.7,
                          textAlign: 'center',
                        }}
                      >
                        This is a private community. A community administrator will review your
                        request and you'll be notified once it's approved.
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    {/* Join Invitation UI */}
                    <Box
                      sx={{
                        display: 'inline-flex',
                        p: 3,
                        borderRadius: '50%',
                        bgcolor: alpha('#9D1500', 0.1),
                        color: 'primary.main',
                        mb: 3,
                      }}
                    >
                      <Users02Icon width={48} height={48} />
                    </Box>

                    <Typography
                      variant="h4"
                      component="h1"
                      sx={{
                        fontWeight: 700,
                        color: 'secondary.900',
                        mb: 2,
                        fontSize: { xs: '1.75rem', sm: '2rem' },
                        textAlign: 'center',
                      }}
                    >
                      Community Invitation
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        color: 'secondary.700',
                        mb: 3,
                        fontWeight: 500,
                        fontSize: '1.125rem',
                        textAlign: 'center',
                      }}
                    >
                      You've been invited to join this community!
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: 'secondary.600',
                        mb: 4,
                        lineHeight: 1.7,
                        maxWidth: 480,
                        mx: 'auto',
                        textAlign: 'center',
                      }}
                    >
                      Join to participate in discussions, collaborate with members, and access save
                      together opportunities.
                    </Typography>
                  </>
                )}

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  justifyContent="center"
                  alignItems="center"
                >
                  {!pendingJoinStatus.isPending && !isPendingMembership && (
                    <Button
                      startIcon={<Users02Icon />}
                      onClick={handleJoinCommunity}
                      variant="contained"
                      disabled={joinCommunityMutation.isPending}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
                        },
                      }}
                    >
                      {joinCommunityMutation.isPending ? 'Joining...' : 'Join Community'}
                    </Button>
                  )}
                  <Button
                    startIcon={<ArrowLeftIcon />}
                    onClick={handleBack}
                    variant="outlined"
                    sx={{
                      borderColor: 'secondary.300',
                      color: 'secondary.700',
                      fontWeight: 500,
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: 'secondary.400',
                        bgcolor: 'secondary.50',
                      },
                    }}
                  >
                    Back to Communities
                  </Button>
                </Stack>
              </Paper>
            </Container>
          </Box>
        );
      }

      // Default access denied message
      return (
        <Box sx={{ minHeight: '100vh', py: 4 }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 3,
              borderRadius: '50%',
              bgcolor: alpha('#ff9800', 0.1),
              color: '#e65100',
              mb: 3,
            }}
          >
            <Shield01Icon width={48} height={48} />
          </Box>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: 'secondary.900',
              mb: 2,
              fontSize: { xs: '1.75rem', sm: '2rem' },
            }}
          >
            Access Restricted
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'secondary.700',
              mb: 3,
              fontWeight: 500,
              fontSize: '1.125rem',
            }}
          >
            Oops! You don't have permission to view this community
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'secondary.600',
              mb: 4,
              lineHeight: 1.7,
              maxWidth: 480,
              mx: 'auto',
            }}
          >
            This community may be private, invite-only, or you might not be a member yet. Contact
            the community administrator if you believe this is an error.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              startIcon={<ArrowLeftIcon />}
              onClick={handleBack}
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
                },
              }}
            >
              Back to Communities
            </Button>
          </Stack>
        </Box>
      );
    }

    // For other errors, show the generic error message
    if (error) {
      return (
        <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
          <Alert severity="error">Error loading community: {error.message}</Alert>
          <Button startIcon={<ArrowLeftIcon />} onClick={handleBack}>
            Back to Communities
          </Button>
        </Stack>
      );
    }
  }

  if (!community) {
    return (
      <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
        <Alert severity="warning">Community not found</Alert>
        <Button startIcon={<ArrowLeftIcon />} onClick={handleBack}>
          Back to Communities
        </Button>
      </Stack>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: 2 }}>
      <Container maxWidth="xl" sx={{ pt: { xs: 0.5, sm: 1 } }}>
        <Stack direction="column" spacing={{ xs: 1.5, sm: 2.5 }}>
          {/* Community Info Card */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              background: `linear-gradient(135deg, ${alpha('#9D1500', 0.02)} 0%, transparent 100%)`,
              position: 'relative',
            }}
          >
            {/* Kebab Menu - Right Side */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 16 },
                right: { xs: 8, sm: 16 },
                zIndex: 1,
              }}
            >
              <IconButton
                onClick={event => setCommunityMenuAnchor(event.currentTarget)}
                sx={{
                  color: 'secondary.400',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: alpha('#9D1500', 0.08),
                  },
                }}
              >
                <DotsVerticalIcon />
              </IconButton>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 3 }}
              alignItems={{ xs: 'center', sm: 'flex-start' }}
            >
              <Avatar
                {...getAvatarProps(community.name)}
                sx={{
                  width: { xs: 56, sm: 64 },
                  height: { xs: 56, sm: 64 },
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  fontWeight: 700,
                  bgcolor: 'primary.main',
                  color: 'white',
                  boxShadow: '0px 4px 20px rgba(157, 21, 0, 0.2)',
                }}
              />
              <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: 'secondary.900',
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {community.name}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'secondary.700',
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    lineHeight: 1.6,
                    mb: 0,
                    fontWeight: 400,
                  }}
                >
                  {community.description}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Tab Navigation */}
          <Tabs
            value={selectedTab}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            onChange={(_, newValue) => handleTabChange(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: { xs: 0, sm: 'auto' },
                px: { xs: 1, sm: 2 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          >
            <Tab
              value={TAB_NAMES.HOME}
              label="Community Hub"
              icon={<MessageCircle01Icon width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />}
              iconPosition="start"
              sx={{ gap: 1 }}
            />
            <Tab
              value={TAB_NAMES.NEGOTIATIONS}
              label="Save Together"
              icon={<PiggyBank02Icon width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />}
              iconPosition="start"
              sx={{ gap: 1 }}
            />
            <Tab
              value={TAB_NAMES.CALENDAR}
              label="Community Calendar"
              icon={<CalendarIcon width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />}
              iconPosition="start"
              sx={{ gap: 1.5 }}
            />
            <Tab
              value={TAB_NAMES.CONTACTS}
              label="Contacts"
              icon={<User01Icon width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />}
              iconPosition="start"
              sx={{ gap: 1 }}
            />
            <Tab
              value={TAB_NAMES.MEMBERS}
              label="Members"
              icon={<Users02Icon width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />}
              iconPosition="start"
              sx={{ gap: 1 }}
            />
          </Tabs>

          {/* Tab Content */}
          {selectedTab === TAB_NAMES.HOME &&
            (isMobile ? (
              <>
                {/* 1) Community Info Dropdown */}
                <Paper
                  elevation={0}
                  sx={{
                    mb: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    onClick={() => setCommunityInfoOpen(!communityInfoOpen)}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: 'background.paper',
                      color: 'secondary.900',
                      transition: 'all 0.3s ease',
                      userSelect: 'none',
                      outline: 'none',
                      WebkitTapHighlightColor: 'transparent',
                      '&:hover': {
                        bgcolor: alpha('#9D1500', 0.02),
                      },
                      '&:active': {
                        bgcolor: alpha('#9D1500', 0.02),
                      },
                      '&:focus': {
                        outline: 'none',
                        bgcolor: 'background.paper',
                      },
                      '&:focus-visible': {
                        outline: 'none',
                        bgcolor: 'background.paper',
                      },
                      '&:focus-within': {
                        outline: 'none',
                        bgcolor: 'background.paper',
                      },
                      '& *': {
                        outline: 'none',
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Community Info
                    </Typography>
                    <Box
                      sx={{
                        transform: communityInfoOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                      }}
                    >
                      <Box
                        sx={{
                          width: 0,
                          height: 0,
                          borderLeft: '4px solid transparent',
                          borderRight: '4px solid transparent',
                          borderTop: '6px solid currentColor',
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      overflow: 'hidden',
                      transition: 'max-height 0.3s ease-in-out',
                      maxHeight: communityInfoOpen ? '500px' : '0px',
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        borderRadius: '0 0 8px 8px',
                      }}
                    >
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 6 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box
                            sx={{
                              width: { xs: 32, sm: 36 },
                              height: { xs: 32, sm: 36 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 2,
                              bgcolor: alpha('#9D1500', 0.1),
                              color: 'primary.main',
                              '& svg': {
                                display: 'block',
                              },
                            }}
                          >
                            <CalendarIcon width={isMobile ? 20 : 23} height={isMobile ? 20 : 23} />
                          </Box>
                          <Box>
                            <Typography variant="body2" color="secondary.600">
                              {formatDate(community.created_at)}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box
                            onClick={() => handleTabChange(TAB_NAMES.MEMBERS)}
                            sx={{
                              width: { xs: 32, sm: 36 },
                              height: { xs: 32, sm: 36 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 2,
                              bgcolor: alpha('#9D1500', 0.1),
                              color: 'primary.main',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                            }}
                          >
                            <Users02Icon width={isMobile ? 10 : 12} height={isMobile ? 10 : 12} />
                          </Box>
                          <Stack
                            direction="row"
                            alignItems="baseline"
                            spacing={0.5}
                            onClick={() => handleTabChange(TAB_NAMES.MEMBERS)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <Typography variant="body2" color="secondary.600">
                              {community.member_count}
                            </Typography>
                            <Typography variant="body2" color="secondary.600">
                              {community.member_count === 1 ? 'Member' : 'Members'}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>

                      {/* Status Information */}
                      <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" gap={1}>
                        <Chip
                          label={community.user_role === 'ADMIN' ? 'Admin' : 'Member'}
                          size="small"
                          sx={{
                            bgcolor:
                              community.user_role === 'ADMIN' ? 'primary.main' : 'secondary.200',
                            color: community.user_role === 'ADMIN' ? 'white' : 'secondary.700',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                        <Chip
                          label={community.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            bgcolor: community.is_active
                              ? alpha('#4caf50', 0.1)
                              : alpha('#f44336', 0.1),
                            color: community.is_active ? '#2e7d32' : '#d32f2f',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                      </Stack>
                    </Box>
                  </Box>
                </Paper>

                {/* 2) Rules Dropdown */}
                <Paper
                  elevation={0}
                  sx={{
                    mb: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    onClick={() => setRulesOpen(!rulesOpen)}
                    sx={{
                      p: 1.875,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: 'background.paper',
                      color: 'secondary.900',
                      transition: 'all 0.3s ease',
                      userSelect: 'none',
                      outline: 'none',
                      WebkitTapHighlightColor: 'transparent',
                      '&:hover': {
                        bgcolor: alpha('#9D1500', 0.02),
                      },
                      '&:active': {
                        bgcolor: alpha('#9D1500', 0.02),
                      },
                      '&:focus': {
                        outline: 'none',
                        bgcolor: 'background.paper',
                      },
                      '&:focus-visible': {
                        outline: 'none',
                        bgcolor: 'background.paper',
                      },
                      '&:focus-within': {
                        outline: 'none',
                        bgcolor: 'background.paper',
                      },
                      '& *': {
                        outline: 'none',
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ width: '100%' }}
                    >
                      <Typography
                        sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Rules
                      </Typography>
                      {community.user_role === 'ADMIN' && (
                        <Button
                          startIcon={community.rules ? <EditIcon /> : <PlusIcon />}
                          onClick={e => {
                            e.stopPropagation();
                            setEditRulesModalOpen(true);
                          }}
                          variant="outlined"
                          size="small"
                          sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            fontWeight: 500,
                            px: 0.75,
                            py: 0,
                            borderRadius: 1,
                            textTransform: 'none',
                            fontSize: '0.7rem',
                            minWidth: 'auto',
                            height: 24,
                            '&:hover': {
                              bgcolor: alpha('#9D1500', 0.08),
                              borderColor: 'primary.dark',
                            },
                          }}
                        >
                          {community.rules ? 'Edit' : 'Add'}
                        </Button>
                      )}
                    </Stack>
                    <Box
                      sx={{
                        transform: rulesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        ml: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 0,
                          height: 0,
                          borderLeft: '4px solid transparent',
                          borderRight: '4px solid transparent',
                          borderTop: '6px solid currentColor',
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      overflow: 'hidden',
                      transition: 'max-height 0.3s ease-in-out',
                      maxHeight: rulesOpen ? '500px' : '0px',
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        borderRadius: '0 0 8px 8px',
                      }}
                    >
                      {community.rules ? (
                        <Box
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            borderRadius: 2,
                            bgcolor: alpha('#9D1500', 0.02),
                            border: '1px solid',
                            borderColor: alpha('#9D1500', 0.1),
                          }}
                        >
                          <Box
                            component="ol"
                            sx={{
                              m: 0,
                              p: 0,
                              pl: 3,
                              listStyleType: 'decimal',
                              '& li': {
                                mb: 1.5,
                                lineHeight: 1.5,
                                color: 'secondary.800',
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                pl: 1,
                              },
                              '& li:last-child': {
                                mb: 0,
                              },
                            }}
                          >
                            {community.rules
                              .split('\n')
                              .filter(line => line.trim())
                              .map(rule => (
                                <Box
                                  component="li"
                                  key={`rule-${rule.trim().replace(/\s+/g, '-').substring(0, 30)}`}
                                >
                                  {rule.trim()}
                                </Box>
                              ))}
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            textAlign: 'center',
                            borderRadius: 2,
                            bgcolor: 'secondary.50',
                            border: '2px dashed',
                            borderColor: 'secondary.200',
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="secondary.600"
                            sx={{ fontStyle: 'italic', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            {community.user_role === 'ADMIN'
                              ? 'No community rules set. Click "Add" to create guidelines.'
                              : 'No community rules have been set yet.'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>

                {/* 3) Community Discussions */}
                <Paper
                  elevation={0}
                  sx={{
                    mb: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      p: 1.875,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: 'background.paper',
                      color: 'secondary.900',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Community Discussions
                    </Typography>
                    {community?.user_role && (
                      <Button
                        startIcon={<PlusIcon />}
                        variant="contained"
                        onClick={handleCreatePostClick}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 500,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                            boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
                          },
                        }}
                      >
                        Create Post
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ p: 2 }}>
                    {(() => {
                      if (!posts || posts.length === 0) {
                        return (
                          <Box
                            sx={{
                              textAlign: 'center',
                              py: 4,
                              px: 3,
                              borderRadius: 2,
                              border: '2px dashed',
                              borderColor: 'secondary.200',
                              bgcolor: alpha('#f5f5f5', 0.5),
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="secondary.600"
                              sx={{ fontStyle: 'italic' }}
                            >
                              No discussions yet. Be the first to start a conversation!
                            </Typography>
                          </Box>
                        );
                      }

                      return posts.map((post: PostResponse, index: number) => (
                        <PostDiscussion
                          key={post.id}
                          post={post}
                          index={index}
                          totalPosts={posts.length}
                          communityId={parseInt(id, 10)}
                          replyInputs={replyInputs}
                          showReplyForms={showReplyForms}
                          expandedReplies={expandedReplies}
                          onTogglePostLike={handleTogglePostLike}
                          onShowReplyForm={handleShowReplyForm}
                          onReplyInputChange={handleReplyInputChange}
                          onSubmitReply={handleSubmitReply}
                          onToggleExpandedReplies={handleToggleExpandedReplies}
                          onToggleReplyLike={handleToggleReplyLike}
                          getAvatarProps={getAvatarProps}
                          togglePostLikeMutation={togglePostLikeMutation}
                          createReplyMutation={createReplyMutation}
                        />
                      ));
                    })()}
                  </Box>
                </Paper>

                {/* 4) Recent Activity Dropdown */}
                <Paper
                  elevation={0}
                  sx={{
                    mb: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    onClick={() => setRecentActivityOpen(!recentActivityOpen)}
                    sx={{
                      p: 1.875,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: 'background.paper',
                      color: 'secondary.900',
                      transition: 'all 0.3s ease',
                      userSelect: 'none',
                      outline: 'none',
                      WebkitTapHighlightColor: 'transparent',
                      '&:hover': {
                        bgcolor: alpha('#9D1500', 0.02),
                      },
                      '&:active': {
                        bgcolor: alpha('#9D1500', 0.02),
                      },
                      '&:focus': {
                        outline: 'none',
                        bgcolor: 'background.paper',
                      },
                      '&:focus-visible': {
                        outline: 'none',
                        bgcolor: 'background.paper',
                      },
                      '&:focus-within': {
                        outline: 'none',
                        bgcolor: 'background.paper',
                      },
                      '& *': {
                        outline: 'none',
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Recent Activity
                    </Typography>
                    <Box
                      sx={{
                        transform: recentActivityOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                      }}
                    >
                      <Box
                        sx={{
                          width: 0,
                          height: 0,
                          borderLeft: '4px solid transparent',
                          borderRight: '4px solid transparent',
                          borderTop: '6px solid currentColor',
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      overflow: 'hidden',
                      transition: 'max-height 0.3s ease-in-out',
                      maxHeight: recentActivityOpen ? '500px' : '0px',
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        borderRadius: '0 0 8px 8px',
                      }}
                    >
                      <RecentActivity
                        activities={activities || []}
                        isLoading={activitiesLoading}
                        communityId={parseInt(id, 10)}
                      />
                    </Box>
                  </Box>
                </Paper>
              </>
            ) : (
              /* Desktop: existing two-column layout */
              <Stack direction="row" spacing={3} sx={{ alignItems: 'flex-start' }}>
                {/* Main Discussions Panel - 2/3 on desktop */}
                <Stack spacing={3} sx={{ flex: 2, width: '100%' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      height: 'fit-content',
                    }}
                  >
                    <CommunityPostsTab
                      communityId={parseInt(id, 10)}
                      isUserMember={!!community?.user_role}
                      isUserAdmin={community?.user_role === 'ADMIN'}
                      onJoinCommunity={handleJoinCommunity}
                    />
                  </Paper>
                </Stack>

                {/* Sidebar - 1/3 on desktop */}
                <Stack spacing={3} sx={{ flex: 1, width: '100%' }}>
                  {/* Community Info */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: 'secondary.900', mb: 2, pt: 1 }}
                    >
                      Community Info
                    </Typography>

                    {/* Creation Date and Member Count */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 6 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: { xs: 32, sm: 36 },
                            height: { xs: 32, sm: 36 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 2,
                            bgcolor: alpha('#9D1500', 0.1),
                            color: 'primary.main',
                            '& svg': {
                              display: 'block',
                            },
                          }}
                        >
                          <CalendarIcon width={isMobile ? 20 : 23} height={isMobile ? 20 : 23} />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="secondary.600">
                            {formatDate(community.created_at)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          onClick={() => handleTabChange(TAB_NAMES.MEMBERS)}
                          sx={{
                            width: { xs: 32, sm: 36 },
                            height: { xs: 32, sm: 36 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 2,
                            bgcolor: alpha('#9D1500', 0.1),
                            color: 'primary.main',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                          }}
                        >
                          <Users02Icon width={isMobile ? 10 : 12} height={isMobile ? 10 : 12} />
                        </Box>
                        <Stack
                          direction="row"
                          alignItems="baseline"
                          spacing={0.5}
                          onClick={() => handleTabChange(TAB_NAMES.MEMBERS)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Typography variant="body2" color="secondary.600">
                            {community.member_count}
                          </Typography>
                          <Typography variant="body2" color="secondary.600">
                            {community.member_count === 1 ? 'Member' : 'Members'}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>

                    {/* Status Information */}
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" gap={1}>
                      <Chip
                        label={community.user_role === 'ADMIN' ? 'Admin' : 'Member'}
                        size="small"
                        sx={{
                          bgcolor:
                            community.user_role === 'ADMIN' ? 'primary.main' : 'secondary.200',
                          color: community.user_role === 'ADMIN' ? 'white' : 'secondary.700',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                      <Chip
                        label={community.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: community.is_active
                            ? alpha('#4caf50', 0.1)
                            : alpha('#f44336', 0.1),
                          color: community.is_active ? '#2e7d32' : '#d32f2f',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Stack>
                  </Paper>

                  {/* Community Rules */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2, pt: 1 }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.900' }}>
                        Rules
                      </Typography>
                      {community.user_role === 'ADMIN' && (
                        <Button
                          startIcon={community.rules ? <EditIcon /> : <PlusIcon />}
                          onClick={() => setEditRulesModalOpen(true)}
                          variant="outlined"
                          size="small"
                          sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            fontWeight: 500,
                            px: { xs: 1.5, sm: 2 },
                            py: 0.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            '&:hover': {
                              bgcolor: alpha('#9D1500', 0.08),
                              borderColor: 'primary.dark',
                            },
                          }}
                        >
                          {community.rules ? 'Edit' : 'Add'}
                        </Button>
                      )}
                    </Stack>

                    {community.rules ? (
                      <Box
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          bgcolor: alpha('#9D1500', 0.02),
                          border: '1px solid',
                          borderColor: alpha('#9D1500', 0.1),
                        }}
                      >
                        <Box
                          component="ol"
                          sx={{
                            m: 0,
                            p: 0,
                            pl: 3,
                            listStyleType: 'decimal',
                            '& li': {
                              mb: 1.5,
                              lineHeight: 1.5,
                              color: 'secondary.800',
                              fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              pl: 1,
                            },
                            '& li:last-child': {
                              mb: 0,
                            },
                          }}
                        >
                          {community.rules
                            .split('\n')
                            .filter(line => line.trim())
                            .map(rule => (
                              <Box
                                component="li"
                                key={`rule-${rule.trim().replace(/\s+/g, '-').substring(0, 30)}`}
                              >
                                {rule.trim()}
                              </Box>
                            ))}
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          textAlign: 'center',
                          borderRadius: 2,
                          bgcolor: 'secondary.50',
                          border: '2px dashed',
                          borderColor: 'secondary.200',
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="secondary.600"
                          sx={{ fontStyle: 'italic', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {community.user_role === 'ADMIN'
                            ? 'No community rules set. Click "Add" to create guidelines.'
                            : 'No community rules have been set yet.'}
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  {/* Recent Activity - Compact Version */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}
                    >
                      Recent Activity
                    </Typography>
                    <RecentActivity
                      activities={activities || []}
                      isLoading={activitiesLoading}
                      communityId={parseInt(id, 10)}
                    />
                  </Paper>
                </Stack>
              </Stack>
            ))}

          {selectedTab === TAB_NAMES.MEMBERS && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: 'secondary.darker',
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  Members
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'secondary.darker',
                      mx: 1,
                      verticalAlign: 'middle',
                    }}
                  />
                  {community.member_count}
                  {community.user_role === 'ADMIN' &&
                    community.pending_count > 0 &&
                    `, ${community.pending_count} pending`}
                </Typography>

                <Button
                  startIcon={<PlusIcon />}
                  variant="contained"
                  onClick={() => {
                    setShareCommunityModalOpen(true);
                  }}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    fontWeight: 500,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
                    },
                  }}
                >
                  Invite Member
                </Button>
              </Box>
              <Stack spacing={{ xs: 1.5, sm: 2 }}>
                {community.members?.map(member => (
                  <Paper
                    key={member.id}
                    elevation={0}
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'secondary.100',
                      bgcolor: 'secondary.50',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: alpha('#9D1500', 0.2),
                        bgcolor: alpha('#9D1500', 0.02),
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={3}>
                      <Avatar
                        {...getAvatarProps(member.full_name || member.email)}
                        sx={{
                          width: 48,
                          height: 48,
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          bgcolor: 'primary.main',
                          color: 'white',
                        }}
                      >
                        {(member.full_name || member.email).charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.900' }}>
                            {member.full_name || member.email}
                          </Typography>
                          <Chip
                            label={member.role}
                            size="small"
                            sx={{
                              bgcolor: member.role === 'ADMIN' ? 'primary.main' : 'secondary.200',
                              color: member.role === 'ADMIN' ? 'white' : 'secondary.700',
                              fontWeight: 500,
                            }}
                          />
                          {member.status === 'PENDING' && (
                            <Chip
                              label="Pending Approval"
                              size="small"
                              sx={{
                                bgcolor: alpha('#ff9800', 0.1),
                                color: '#e65100',
                                fontWeight: 500,
                              }}
                            />
                          )}
                          {!member.is_verified && member.status !== 'PENDING' && (
                            <Chip
                              label="Unverified"
                              size="small"
                              sx={{
                                bgcolor: alpha('#ff9800', 0.1),
                                color: '#e65100',
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" color="secondary.600">
                            Joined {formatDate(member.joined_at)}
                          </Typography>
                        </Stack>
                      </Box>
                      {community.user_role === 'ADMIN' &&
                        member.id !== community.created_by &&
                        (member.status === 'PENDING' ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                          >
                            <Tooltip title="Approve membership">
                              <IconButton
                                size="small"
                                onClick={() => handleApproveMember(member.membership_id)}
                                disabled={approveMembershipMutation.isPending}
                                sx={{
                                  color: '#4caf50',
                                  '&:hover': {
                                    bgcolor: alpha('#4caf50', 0.08),
                                  },
                                }}
                              >
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject membership">
                              <IconButton
                                size="small"
                                onClick={() => handleRejectMember(member.membership_id)}
                                disabled={rejectMembershipMutation.isPending}
                                sx={{
                                  color: '#f44336',
                                  '&:hover': {
                                    bgcolor: alpha('#f44336', 0.08),
                                  },
                                }}
                              >
                                <XIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Tooltip title="Member options">
                            <IconButton
                              size="small"
                              onClick={event => handleMemberMenuClick(event, member)}
                              sx={{
                                color: 'secondary.400',
                                alignSelf: { xs: 'flex-end', sm: 'center' },
                                '&:hover': {
                                  color: 'primary.main',
                                  bgcolor: alpha('#9D1500', 0.08),
                                },
                              }}
                            >
                              <DotsVerticalIcon />
                            </IconButton>
                          </Tooltip>
                        ))}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          )}

          {selectedTab === TAB_NAMES.NEGOTIATIONS && !isPendingMembership && (
            <GroupNegotiationsSection
              communityId={parseInt(id, 10)}
              groupNegotiations={groupNegotiations}
              isLoading={negotiationsLoading}
              isActive={community?.is_active || false}
              userRole={community?.user_role || undefined}
            />
          )}

          {selectedTab === TAB_NAMES.CALENDAR && (
            <CommunityCalendarView
              communityId={parseInt(id, 10)}
              communityName={community?.name || ''}
              isUserMember={!!community?.user_role}
              isUserAdmin={community?.user_role === 'ADMIN'}
            />
          )}
          {selectedTab === TAB_NAMES.CONTACTS && (
            <CommunityContactsTab
              communityId={parseInt(id, 10)}
              isUserAdmin={community?.user_role === 'ADMIN'}
            />
          )}

          {/* Edit Rules Modal */}
          {editRulesModalOpen && (
            <EditRulesModal
              communityId={parseInt(id, 10)}
              currentRules={community?.rules || null}
              onClose={() => setEditRulesModalOpen(false)}
            />
          )}

          {/* Community Settings Modal */}
          {settingsModalOpen && (
            <CommunitySettingsModal
              communityId={parseInt(id, 10)}
              currentSettings={{
                name: community?.name || '',
                description: community?.description || '',
                require_verification: community?.require_verification || false,
                is_active: community?.is_active || false,
              }}
              onClose={() => setSettingsModalOpen(false)}
            />
          )}

          {/* Community Options Menu */}
          <Menu
            anchorEl={communityMenuAnchor}
            open={Boolean(communityMenuAnchor)}
            onClose={() => setCommunityMenuAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
                  minWidth: 120,
                  py: 0.5,
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setSettingsModalOpen(true);
                setCommunityMenuAnchor(null);
              }}
              sx={{
                py: 1,
                px: 2,
                '&:hover': {
                  bgcolor: alpha('#9D1500', 0.08),
                },
              }}
            >
              <Settings01Icon width={14} height={14} />
              <Typography variant="body2" sx={{ ml: 1, fontSize: '0.875rem' }}>
                Settings
              </Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleLeaveCommunity();
                setCommunityMenuAnchor(null);
              }}
              disabled={leaveCommunityMutation.isPending}
              sx={{
                py: 1,
                px: 2,
                color: 'error.main',
                '&:hover': {
                  bgcolor: alpha('#f44336', 0.08),
                },
              }}
            >
              <LogOut01Icon width={14} height={14} />
              <Typography variant="body2" sx={{ ml: 1, fontSize: '0.875rem' }}>
                Leave Community
              </Typography>
            </MenuItem>
          </Menu>

          {/* Member Options Menu */}
          <Menu
            anchorEl={memberMenuAnchor}
            open={Boolean(memberMenuAnchor)}
            onClose={handleMemberMenuClose}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                  minWidth: 150,
                },
              },
            }}
          >
            <MenuItem
              onClick={handleMemberMenuRemove}
              disabled={removeMemberMutation.isPending}
              sx={{
                color: 'error.main',
                '&:hover': {
                  bgcolor: alpha('#f44336', 0.08),
                },
              }}
            >
              <Trash01Icon width={16} height={16} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Remove Member
              </Typography>
            </MenuItem>
          </Menu>

          {/* Remove Member Confirmation Dialog */}
          <Dialog
            open={removeMemberDialogOpen}
            onClose={() => setRemoveMemberDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                p: 1,
                mx: { xs: 2, sm: 0 },
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                color: 'secondary.900',
                pb: 1,
              }}
            >
              Remove Member
            </DialogTitle>
            <DialogContent sx={{ pb: 2 }}>
              <Typography variant="body1" sx={{ mb: 2, color: 'secondary.700' }}>
                Are you sure you want to remove <strong>{memberToRemove?.name}</strong> from the
                community?
              </Typography>
              <Typography variant="body2" color="secondary.600">
                This action cannot be undone. The member will lose access to all community
                activities, discussions, and save together opportunities. They can rejoin later if
                the community allows it.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
              <Button
                onClick={() => setRemoveMemberDialogOpen(false)}
                sx={{
                  color: 'secondary.600',
                  fontWeight: 500,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '&:hover': {
                    bgcolor: 'secondary.100',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmRemoveMember}
                disabled={removeMemberMutation.isPending}
                variant="contained"
                sx={{
                  bgcolor: '#f44336',
                  color: 'white',
                  fontWeight: 500,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  boxShadow: '0px 2px 8px rgba(244, 67, 54, 0.2)',
                  '&:hover': {
                    bgcolor: '#d32f2f',
                    boxShadow: '0px 4px 12px rgba(244, 67, 54, 0.3)',
                  },
                  '&:disabled': {
                    bgcolor: 'secondary.300',
                    color: 'secondary.500',
                  },
                }}
              >
                {removeMemberMutation.isPending ? 'Removing...' : 'Remove Member'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Leave Community Confirmation Dialog */}
          <Dialog
            open={leaveConfirmDialogOpen}
            onClose={() => setLeaveConfirmDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                p: 1,
                mx: { xs: 2, sm: 0 },
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                color: 'secondary.900',
                pb: 1,
              }}
            >
              Leave Community
            </DialogTitle>
            <DialogContent sx={{ pb: 2 }}>
              <Typography variant="body1" sx={{ mb: 2, color: 'secondary.700' }}>
                Are you sure you want to leave <strong>{community?.name}</strong>?
              </Typography>
              <Typography variant="body2" color="secondary.600">
                You will no longer have access to community activities, discussions, and save
                together opportunities. You can rejoin later if the community is still active.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
              <Button
                onClick={() => setLeaveConfirmDialogOpen(false)}
                sx={{
                  color: 'secondary.600',
                  fontWeight: 500,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '&:hover': {
                    bgcolor: 'secondary.100',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmLeave}
                disabled={leaveCommunityMutation.isPending}
                variant="contained"
                sx={{
                  bgcolor: '#f44336',
                  color: 'white',
                  fontWeight: 500,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  boxShadow: '0px 2px 8px rgba(244, 67, 54, 0.2)',
                  '&:hover': {
                    bgcolor: '#d32f2f',
                    boxShadow: '0px 4px 12px rgba(244, 67, 54, 0.3)',
                  },
                  '&:disabled': {
                    bgcolor: 'secondary.300',
                    color: 'secondary.500',
                  },
                }}
              >
                {leaveCommunityMutation.isPending ? 'Leaving...' : 'Leave Community'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Create Post Dialog */}
          <Dialog
            open={showCreatePostForm}
            onClose={handleCancelCreatePost}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                p: 1,
                mx: { xs: 2, sm: 0 },
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                color: 'secondary.900',
                pb: 1,
              }}
            >
              Create New Post
            </DialogTitle>
            <DialogContent sx={{ pb: 2 }}>
              <PostForm
                onSubmit={handleCreatePost}
                onCancel={handleCancelCreatePost}
                mode="create"
              />
            </DialogContent>
          </Dialog>

          {/* Share Community Modal */}
          {community && (
            <ShareCommunityModal
              open={shareCommunityModalOpen}
              onClose={() => setShareCommunityModalOpen(false)}
              communityName={community.name}
              communityId={parseInt(id, 10)}
            />
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default CommunityViewPage;
